import { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { MaIcon, MaTimePill } from "../components/ui";
import { useMagicLinkRequest, useMagicLinkVerify } from "../lib/queries";

export function Login() {
  const navigate = useNavigate();
  const location = useLocation();
  const request = useMagicLinkRequest();
  const verify = useMagicLinkVerify();
  const returnTo = (location.state as { returnTo?: string } | null)?.returnTo ?? "/discover";

  const [email, setEmail] = useState("");
  const [sent, setSent] = useState<{ devCode?: string } | null>(null);
  const [code, setCode] = useState("");
  // verifyError holds the server-returned error key so we can route between
  // "wrong code, try again" and "burned — request a new code" CTAs.
  const [verifyError, setVerifyError] = useState<"invalid_code" | "too_many_attempts" | "other" | null>(null);

  const onSubmitEmail = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const result = await request.mutateAsync({ email });
      setSent({ devCode: result.devCode });
      setCode("");
      setVerifyError(null);
    } catch { /* error rendered below */ }
  };

  const onSubmitCode = async (e: React.FormEvent) => {
    e.preventDefault();
    setVerifyError(null);
    try {
      await verify.mutateAsync({ email, code });
      // Token set inside the mutation's onSuccess — safe to navigate now.
      navigate(returnTo, { replace: true });
    } catch (err: any) {
      const msg = String(err?.message ?? "");
      if (msg.includes("too_many_attempts")) setVerifyError("too_many_attempts");
      else if (msg.includes("invalid_code") || msg.includes("invalid_input")) setVerifyError("invalid_code");
      else setVerifyError("other");
    }
  };

  const reset = () => {
    setSent(null); setEmail(""); setCode(""); setVerifyError(null);
  };

  if (sent) {
    return (
      <div className="ma-screen">
        <div style={{
          padding: "calc(env(safe-area-inset-top, 20px) + 34px) 24px 0",
          display: "flex", justifyContent: "space-between", alignItems: "center",
        }}>
          <button onClick={reset} style={backBtn}>← Back</button>
          <MaTimePill />
        </div>
        <div style={{ padding: "44px 24px 0" }}>
          <div className="ma-mono" style={{
            fontSize: 10, letterSpacing: "0.2em", textTransform: "uppercase",
            color: "var(--ma-brick)", marginBottom: 10,
          }}>check your inbox</div>
          <div className="ma-display" style={{ fontSize: 38, lineHeight: 0.95, color: "var(--ma-ink)", marginBottom: 12 }}>
            Enter your code
          </div>
          <div style={{ fontFamily: "var(--ma-body)", fontSize: 15, lineHeight: 1.45, color: "var(--ma-ink-soft)", marginBottom: 20 }}>
            We sent a 6-digit code to <strong style={{ color: "var(--ma-ink)" }}>{email}</strong>. It expires in 30 minutes.
          </div>

          <form onSubmit={onSubmitCode}>
            <input
              type="text"
              inputMode="numeric"
              pattern="[0-9]*"
              autoComplete="one-time-code"
              maxLength={6}
              required
              autoFocus
              value={code}
              onChange={(e) => { setCode(e.target.value.replace(/\D/g, "").slice(0, 6)); setVerifyError(null); }}
              placeholder="123456"
              style={codeInputStyle}
            />

            <button
              type="submit"
              disabled={code.length !== 6 || verify.isPending}
              style={{
                width: "100%", padding: "16px 0",
                background: "var(--ma-ink)", color: "var(--ma-paper)",
                border: "none", borderRadius: "var(--ma-card-radius)",
                fontFamily: "var(--ma-display)", fontWeight: 700, fontSize: 16,
                letterSpacing: "0.02em", cursor: "pointer",
                display: "flex", alignItems: "center", justifyContent: "center", gap: 10,
                opacity: (code.length !== 6 || verify.isPending) ? 0.5 : 1,
              }}>
              {verify.isPending ? "Signing in…" : "Sign in"}
              <MaIcon name="arrow" size={16} stroke={2} color="var(--ma-paper)" />
            </button>

            {verifyError === "invalid_code" && (
              <div className="ma-mono" style={errorStyle}>
                That code didn't match. Try again.
              </div>
            )}
            {verifyError === "too_many_attempts" && (
              <div style={{
                marginTop: 12, padding: "12px 14px",
                background: "var(--ma-cream)",
                border: "var(--ma-border) solid var(--ma-ink)",
                borderRadius: "var(--ma-card-radius)",
              }}>
                <div className="ma-mono" style={{ ...errorStyle, marginTop: 0, marginBottom: 8 }}>
                  Too many wrong tries. That code is dead now.
                </div>
                <button type="button" onClick={onSubmitEmail as any} style={{
                  padding: "10px 14px",
                  background: "var(--ma-brick)", color: "var(--ma-paper)",
                  border: "var(--ma-border) solid var(--ma-ink)",
                  borderRadius: "var(--ma-card-radius)",
                  fontFamily: "var(--ma-mono)", fontSize: 11, letterSpacing: "0.12em",
                  textTransform: "uppercase", fontWeight: 600, cursor: "pointer",
                }}>
                  Send a new code
                </button>
              </div>
            )}
            {verifyError === "other" && (
              <div className="ma-mono" style={errorStyle}>
                Something went wrong. Try again.
              </div>
            )}
          </form>

          {sent.devCode && (
            <div style={{
              background: "var(--ma-cream)",
              border: "var(--ma-border) solid var(--ma-ink)",
              borderRadius: "var(--ma-card-radius)",
              padding: "12px 14px", marginTop: 18,
            }}>
              <div className="ma-mono" style={{ fontSize: 10, letterSpacing: "0.16em", textTransform: "uppercase", color: "var(--ma-brick)", marginBottom: 4 }}>
                Dev mode — email not configured
              </div>
              <div style={{ fontFamily: "var(--ma-body)", fontSize: 12, color: "var(--ma-ink)" }}>
                Use this code: <strong style={{ fontFamily: "var(--ma-mono)", letterSpacing: "0.12em" }}>{sent.devCode}</strong>
              </div>
            </div>
          )}

          <button onClick={reset} style={{
            background: "transparent", border: "none", padding: 0, marginTop: 20,
            fontFamily: "var(--ma-mono)", fontSize: 11, letterSpacing: "0.12em",
            textTransform: "uppercase", color: "var(--ma-ink-soft)", textDecoration: "underline", cursor: "pointer",
          }}>
            Use a different email
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="ma-screen">
      <div style={{
        padding: "calc(env(safe-area-inset-top, 20px) + 34px) 24px 0",
        display: "flex", justifyContent: "space-between", alignItems: "center",
      }}>
        <button onClick={() => navigate(-1)} style={backBtn}>← Back</button>
        <MaTimePill />
      </div>

      <div style={{ padding: "32px 24px 0" }}>
        <div className="ma-mono" style={{
          fontSize: 10, letterSpacing: "0.2em", textTransform: "uppercase",
          color: "var(--ma-brick)", marginBottom: 10,
        }}>save spots · follow friends</div>
        <div className="ma-display" style={{ fontSize: 44, lineHeight: 0.95, color: "var(--ma-ink)", marginBottom: 28 }}>
          Sign in to<br />myams<span style={{ color: "var(--ma-brick)" }}>.</span>
        </div>
      </div>

      <form onSubmit={onSubmitEmail} style={{ padding: "0 24px 24px" }}>
        <label style={{ display: "block", marginBottom: 16 }}>
          <div className="ma-mono" style={{
            fontSize: 10, letterSpacing: "0.16em",
            textTransform: "uppercase", color: "var(--ma-ink-soft)", marginBottom: 6,
          }}>Email</div>
          <input
            type="email" required autoComplete="email"
            value={email} onChange={(e) => setEmail(e.target.value)}
            placeholder="you@example.com" autoFocus
            style={inputStyle}
          />
        </label>

        <button
          type="submit"
          disabled={request.isPending}
          style={{
            width: "100%", padding: "16px 0",
            background: "var(--ma-ink)", color: "var(--ma-paper)",
            border: "none", borderRadius: "var(--ma-card-radius)",
            fontFamily: "var(--ma-display)", fontWeight: 700, fontSize: 16,
            letterSpacing: "0.02em", cursor: "pointer",
            display: "flex", alignItems: "center", justifyContent: "center", gap: 10,
            opacity: request.isPending ? 0.6 : 1,
          }}>
          {request.isPending ? "Sending…" : "Email me a code"}
          <MaIcon name="arrow" size={16} stroke={2} color="var(--ma-paper)" />
        </button>

        {request.error && (
          <div className="ma-mono" style={errorStyle}>
            Could not send the code. Try again.
          </div>
        )}
      </form>

      <div style={{
        padding: "0 24px 24px", textAlign: "center",
        fontFamily: "var(--ma-body)", fontSize: 13, color: "var(--ma-ink-soft)",
      }}>
        No password needed. We email a 6-digit sign-in code.
      </div>
    </div>
  );
}

const backBtn: React.CSSProperties = {
  background: "transparent", border: "none", padding: 0,
  color: "var(--ma-ink)", fontFamily: "var(--ma-mono)", fontSize: 11,
  letterSpacing: "0.14em", textTransform: "uppercase", cursor: "pointer",
};
const inputStyle: React.CSSProperties = {
  width: "100%", padding: "14px",
  background: "var(--ma-cream)",
  border: "var(--ma-border) solid var(--ma-ink)",
  borderRadius: "var(--ma-card-radius)",
  // 16px is the minimum at which iOS Safari does NOT auto-zoom the
  // viewport on focus. Don't drop below this on any text input.
  fontFamily: "var(--ma-body)", fontSize: 16,
  color: "var(--ma-ink)", boxSizing: "border-box",
};
const codeInputStyle: React.CSSProperties = {
  width: "100%", padding: "16px",
  background: "var(--ma-cream)",
  border: "var(--ma-border) solid var(--ma-ink)",
  borderRadius: "var(--ma-card-radius)",
  // Big monospace block so the 6 digits read cleanly and the iOS-zoom
  // 16-px floor is comfortably cleared.
  fontFamily: "var(--ma-mono)", fontSize: 28,
  letterSpacing: "0.32em", textAlign: "center",
  color: "var(--ma-ink)", boxSizing: "border-box",
  marginBottom: 14,
};
const errorStyle: React.CSSProperties = {
  fontSize: 11, color: "var(--ma-brick)", marginTop: 12,
  textTransform: "uppercase", letterSpacing: "0.12em",
};
